import { Draft } from 'immer';
import {
  CareEnvironment,
  calcFutureValue,
  isNullOrUndefined,
  calcSelfFundingTotalCost,
  calcPolicyProjectedCostCoverage,
  calcPolicyCoverageOfRemainingLtcCosts,
  calcSelfFundingProjectedCostCoverage,
  CarePhase,
  FundingPolicyType,
  calcFundingAnnuityDerivatives,
  calcSelfFundingProjectedROI,
  calcFutureValueSimpleInflation,
  calcSelfFundingTotalsOnClaim,
  carePhaseDefs,
  isCarePhasePast,
  fundingPolicyTypeDefs,
  checkPolicyCalculationTypeEquals,
  calcTotalWithAnnualIncrease,
  calcAnnuityProjectedROI,
} from '@shared';
import { cloneDeep } from 'lodash';
import { calcClientPhaseStartYears } from './clientPhaseStartYears';
import { WritableDraft } from 'immer/dist/internal.js';
import { calcLtcEndYear } from './calcLtcEndYear';
import { analyzeInvestmentReturns } from './calcProjectedROI';

export const unlimitedMaxBenefitMagicNumber = 999999999;

const careEnvironmentToDailyMaxKey: Record<CareEnvironment, keyof PolicyFundingSource> = {
  [CareEnvironment.home]: 'dailyHomeBenefitAmount',
  [CareEnvironment.fullCareFacility]: 'dailyNursingHomeBenefitAmount',
  [CareEnvironment.assistedLiving]: 'dailyAssistedLivingBenefitAmount',
  [CareEnvironment.independentLiving]: 'dailyIndependentLivingBenefitAmount',
};

const careEnvironmentToInflationProtectionKeys: Record<CareEnvironment, keyof PolicyFundingSource> =
  {
    [CareEnvironment.home]: 'dailyHomeInflationProtectionPercent',
    [CareEnvironment.independentLiving]: 'dailyIndependentLivingInflationProtectionPercent',
    [CareEnvironment.assistedLiving]: 'dailyAssistedLivingInflationProtectionPercent',
    [CareEnvironment.fullCareFacility]: 'dailyNursingHomeInflationProtectionPercent',
  };

export function calcFundingSourceDerivatives(mutableClient: Draft<Client>) {
  const commonCalculations = [
    calcFundingAnnuityDerivatives,
    calcCombinedFundingTotalCost,
    calcCombinedProjectedCostCoverage,
    calcCombinedNonLtcPayout,
    calcCombinedTotalValue,
  ];

  calcSourceDefaults(mutableClient);
  runPolicyCalculations(mutableClient);
  runSelfFundingCalculations(mutableClient);
  commonCalculations.forEach(calc => calc(mutableClient));
  calcReturnOnInvestment(mutableClient);
}

function runPolicyCalculations(mutableClient: Draft<Client>) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    clientCalculationSettings: { discountRate },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
  } = mutableClient;

  const calcProjectedCostCoverageFn = clientHasStartedLtc
    ? calcPolicyCoverageOfRemainingLtcCosts
    : calcPolicyProjectedCostCoverage;

  mutableClient.policyPhaseCalculations = {};

  Object.entries(multipleFundingSources.ltcPolicy).forEach(
    ([fundingSourceId, policyFundingSource]) => {
      // initialize ltcPolicy and phaseCalculations
      mutableClient.fundingSources.ltcPolicy = cloneDeep(policyFundingSource);
      mutableClient.phaseCalculations = [];

      calcPolicyTotalCost(mutableClient);
      calcProjectedCostCoverageFn(mutableClient);

      // calculate policy projected ROI
      const {
        policyProjectedReturnOnInvestment,
        policyProjectedCompoundAnnualGrowthRate,
        policyProjectedInternalRateOfReturn,
        investmentPeriodYears,
        investments,
        returns,
      } = calcPolicyProjectedROI(
        mutableClient,
        mutableClient.fundingSources.ltcPolicy,
        discountRate ?? 0,
      );

      // test that total cash flows are equal to the policy total cost
      investmentCashflowEqualsPolicyTotalCost(
        investments,
        mutableClient.fundingSources.ltcPolicy.policyTotalCost!,
      );
      if (!allPhaseInflatedProfessionalShareCostDiscountAmount) {
        // only run this check if there is no discount applied
        // this is because when discounts are applied, the savings are added to the policy total value
        // but the return cash flows are not affected by the discount, so the check will fail
        returnCashflowEqualsPolicyTotalValue(
          returns,
          mutableClient.fundingSources.ltcPolicy.policyTotalValue!,
        );
      }

      const ltcPolicy = mutableClient.fundingSources.ltcPolicy;
      ltcPolicy.policyProjectedReturnOnInvestment = policyProjectedReturnOnInvestment;
      ltcPolicy.policyProjectedCompoundAnnualGrowthRate = policyProjectedCompoundAnnualGrowthRate;
      ltcPolicy.policyProjectedInternalRateOfReturn = policyProjectedInternalRateOfReturn;
      ltcPolicy.investmentPeriodYears = investmentPeriodYears;
      ltcPolicy.investmentCashFlows = investments;
      ltcPolicy.returnCashFlows = returns;

      // update multipleFundingSources
      multipleFundingSources.ltcPolicy[fundingSourceId] = mutableClient.fundingSources.ltcPolicy;
      mutableClient.policyPhaseCalculations[fundingSourceId] = mutableClient.phaseCalculations;
    },
  );

  calcMultiplePolicyTotalFields(mutableClient);
}

function runSelfFundingCalculations(mutableClient: Draft<Client>) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    clientCalculationSettings: { discountRate },
  } = mutableClient;

  const calcFunctions = clientHasStartedLtc
    ? [calcSelfFundingTotalsOnClaim]
    : [calcSelfFundingProjectedCostCoverage, calcSelfFundingTotalCost];

  Object.entries(multipleFundingSources.selfFunding).forEach(
    ([fundingSourceId, selfFundingSource]) => {
      // initialize selfFunding
      mutableClient.fundingSources.selfFunding = cloneDeep(selfFundingSource);

      // run calculations
      calcFunctions.forEach(calc => calc(mutableClient));

      // calculate self-funding projected ROI
      const { investmentPeriodYears } = calcSelfFundingProjectedROI(
        mutableClient,
        discountRate ?? undefined,
      );

      const selfFunding = mutableClient.fundingSources.selfFunding;
      selfFunding.investmentPeriodYears = investmentPeriodYears;

      // update multipleFundingSources
      multipleFundingSources.selfFunding[fundingSourceId] =
        mutableClient.fundingSources.selfFunding;
    },
  );

  calcMultipleSelfFundingTotalFields(mutableClient);
}

function calcSourceDefaults(mutableClient: Draft<Client>) {
  const {
    appliedInferenceSet: { ltcAtYear },
    multipleFundingSources,
  } = mutableClient;

  // Set defaults for all self-funding sources
  Object.values(multipleFundingSources.selfFunding).forEach(selfFunding => {
    calcDetailDefaults(selfFunding as NumberInputSelfFundingSource, {
      contributionStartYear: new Date().getFullYear(),
      contributionEndYear: ltcAtYear,
    });
  });
}

function calcDetailDefaults<T extends {}>(source: T, defaults: Partial<T>) {
  (Object.entries(defaults) as [keyof T, number][]).forEach(
    ([key, defaultValue]) => (source[key] = (source[key] ?? defaultValue) as any),
  );
}

export function calculateMonthsBetweenYears(startYear: number, endYear: number): number {
  const currentYear: number = new Date().getFullYear();
  const monthsInYear: number = 12;

  // Check for invalid input
  if (startYear > endYear) {
    return 0;
  }

  // Calculate the difference in years
  const yearDifference: number = endYear - startYear;

  if (startYear === currentYear) {
    // If the start year is the current year, calculate months passed in the current year
    const monthsPassedInCurrentYear: number = new Date().getMonth() + 1;
    return yearDifference * monthsInYear - monthsPassedInCurrentYear;
  } else {
    // Calculate the total months based on the year difference
    const totalMonths: number = yearDifference * monthsInYear;
    return totalMonths;
  }
}

function getKeyByValue(enumObj: any, value: number): string | undefined {
  for (const key in enumObj) {
    if (enumObj[key] === value) {
      return key;
    }
  }
  return undefined; // Return undefined if no match is found
}

function capitalizeFirstLetter(inputString: string): string {
  if (inputString.length === 0) {
    return inputString; // Return the input string as is if it's empty
  }

  const firstLetter = inputString.charAt(0).toUpperCase();
  const restOfString = inputString.slice(1);

  return firstLetter + restOfString;
}

type PolicyCoverage = {
  portionOfPhaseCostBenefitEligible: number;
  maxPayoutAccordingToDailyMaximum?: number;
  phaseMonthlyCost: number;
  phaseCostPerDayNeeded: number;
  phaseCareDaysNeeded: number;
  daysWhereLessThanTwoADLs?: number;
  applicableWaitingPeriodDays?: number;
  phaseBenefitEligibleCareDays?: number;
  phaseEligibleCareDaysAfter2AdlRequirement?: number;
  phaseEligibleCareDaysAfterWaitingPeriod?: number;
  dailyBenefitAmount?: number;
  inflationProtection?: number | null;
  inflationPeriodYears?: number;
  inflatedDailyBenefitAmount?: number;
  inflatedDailyBenefitAmountBase?: number;
  inflatedDailyBenefitAmountRider?: number;
  inflatedFaceAmount?: number;
  dailyBenefitAmountBase?: number;
  baseBenefitAmountRemaining?: number;
  baseAmountApplicableDays?: number;
  riderAmountApplicableDays?: number;
};

export function calcSinglePhasePolicyCoverage(
  mutableClient: Draft<Client>,
  appliedCareEnvironment: CareEnvironment,
  phaseCosts: SinglePhaseCosts,
  policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator,
): PolicyCoverage {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyInflationProtection,
        policyPremiumStartYear,
        policyInflationProtectionYears,
      },
    },
    clientPhasePredictedEndYears,
  } = mutableClient;

  const isCalculationTypeHybridLifeIra = checkPolicyCalculationTypeEquals(
    ltcPolicy.policyType,
    FundingPolicyType.hybridLifeIra,
  );

  const { phaseCareMonthsNeeded, phaseProfessionalShareCost, phaseInflatedProfessionalShareCost } =
    phaseCosts;

  // Calculate amount of phase cost that is benefit eligible
  const daysInMonth = 30;
  const phaseCareDaysNeeded = phaseCareMonthsNeeded * daysInMonth;
  const phaseMonthlyCost = phaseInflatedProfessionalShareCost / phaseCareMonthsNeeded;
  const phaseCostPerDayNeeded = phaseInflatedProfessionalShareCost / phaseCareDaysNeeded;
  const {
    phaseEligibleCareDays: phaseBenefitEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
  } = handlePhaseBenefitEligibleCareDays(mutableClient, phaseCosts);
  const portionOfPhaseCostBenefitEligible = phaseBenefitEligibleCareDays * phaseCostPerDayNeeded;

  if (phaseProfessionalShareCost === 0) {
    return {
      portionOfPhaseCostBenefitEligible: 0,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }
  // Calculate daily benefit amount and inflation protection
  const dailyBenefitAmountVar = careEnvironmentToDailyMaxKey[appliedCareEnvironment];
  const dailyBenefitAmount = ltcPolicy[dailyBenefitAmountVar] as number | null | undefined;

  if (dailyBenefitAmount === 0) {
    return {
      portionOfPhaseCostBenefitEligible: 0,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }

  const policyPurchaseToPhaseEndYears = Math.max(
    clientPhasePredictedEndYears[phaseCosts.carePhase]! - policyPremiumStartYear!,
    0,
  );
  const inflationPeriodYears = Math.min(
    policyPurchaseToPhaseEndYears,
    policyInflationProtectionYears ?? Infinity,
  );

  // no daily max benefit amount, return portion of phase cost that is benefit eligible
  if (isNullOrUndefined(dailyBenefitAmount) && !isCalculationTypeHybridLifeIra) {
    return {
      portionOfPhaseCostBenefitEligible,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      inflationPeriodYears,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }

  // Calculate inflated daily max benefit amount
  const careEnvironmentInflationProtectionVar =
    careEnvironmentToInflationProtectionKeys[appliedCareEnvironment];

  const careEnvInflationValue = ltcPolicy[careEnvironmentInflationProtectionVar];
  const inflationProtection =
    typeof careEnvInflationValue === 'number' && careEnvInflationValue
      ? careEnvInflationValue
      : policyInflationProtection;

  const {
    maxPayoutAccordingToDailyMaximum,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
  } = calcMaxPayOutAccordingToDailyMaximum(
    mutableClient,
    ltcPolicy,
    policyDataAccumulator,
    phaseBenefitEligibleCareDays,
    dailyBenefitAmount as number,
    inflationPeriodYears,
    phaseCostPerDayNeeded,
  );

  return {
    maxPayoutAccordingToDailyMaximum,
    portionOfPhaseCostBenefitEligible,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    phaseCareDaysNeeded,
    phaseBenefitEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
    inflationProtection,
    inflationPeriodYears,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
    dailyBenefitAmount: dailyBenefitAmount ?? undefined,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
  };
}

function calcMaxPayOutAccordingToDailyMaximum(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  policyDataAccumulator: PolicyDataAccumulator,
  phaseBenefitEligibleCareDays: number,
  dailyBenefitAmount: number,
  inflationPeriodYears: number,
  phaseCostPerDayNeeded: number,
) {
  const {
    policyMaximumBenefitAmount,
    policyInflationProtection,
    policyInflationProtectionOnCOBAmount,
    policyInflationProtectionOnFaceAmount,
    policyMonthlyWithdrawalRate,
    isIndemnityPolicyPayment,
  } = ltcPolicy;
  const isCalculationTypeHybridLifeIra = checkPolicyCalculationTypeEquals(
    ltcPolicy.policyType,
    FundingPolicyType.hybridLifeIra,
  );

  let maxPayoutAccordingToDailyMaximum;
  let baseAmountApplicableDays;
  let riderAmountApplicableDays;
  let dailyBenefitAmountBase;

  const deathBenefitAmount = policyMaximumBenefitAmount ?? 0;
  const totalProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? policyDataAccumulator.projectedCostCoverage +
      policyDataAccumulator.partnerProjectedCostCoverage
    : policyDataAccumulator.projectedCostCoverage;

  const calculateInflatedAmount = (inflationProtection: number | null) =>
    calcInflationProtectionFutureValue(
      mutableClient,
      dailyBenefitAmount ?? 0,
      inflationPeriodYears,
      inflationProtection ?? 0,
    );

  const inflatedDailyBenefitAmounts = {
    policyInflationProtection: calculateInflatedAmount(policyInflationProtection),
    policyInflationProtectionOnFaceAmount: calculateInflatedAmount(
      policyInflationProtectionOnFaceAmount ?? policyInflationProtection,
    ),
    policyInflationProtectionOnCOBAmount: calculateInflatedAmount(
      policyInflationProtectionOnCOBAmount ?? policyInflationProtection,
    ),
  };

  const inflatedFaceAmount = calcInflationProtectionFutureValue(
    mutableClient,
    deathBenefitAmount,
    inflationPeriodYears,
    policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? 0,
  );

  const baseBenefitAmountRemaining = Math.max(inflatedFaceAmount - totalProjectedCostCoverage, 0);

  if (isCalculationTypeHybridLifeIra) {
    const monthlyWithDrawalRate = policyMonthlyWithdrawalRate ?? 0;

    // Cap dailyBenefitAmountBase by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountBase = (inflatedFaceAmount * monthlyWithDrawalRate) / 30;
    dailyBenefitAmountBase = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountBase
      : Math.min(uncappedDailyBenefitAmountBase, phaseCostPerDayNeeded);
    ltcPolicy.dailyBenefitAmountBase = dailyBenefitAmountBase;

    baseAmountApplicableDays = Math.min(
      baseBenefitAmountRemaining / dailyBenefitAmountBase,
      phaseBenefitEligibleCareDays,
    );

    riderAmountApplicableDays = phaseBenefitEligibleCareDays - baseAmountApplicableDays;

    // Cap rider daily benefit amount by phaseCostPerDayNeeded
    const uncappedRiderDailyAmount = policyInflationProtectionOnCOBAmount
      ? inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount
      : inflatedDailyBenefitAmounts.policyInflationProtection;
    const cappedRiderDailyAmount = isIndemnityPolicyPayment
      ? uncappedRiderDailyAmount
      : Math.min(uncappedRiderDailyAmount, phaseCostPerDayNeeded);

    const riderMaximumPayOut = riderAmountApplicableDays * cappedRiderDailyAmount;

    maxPayoutAccordingToDailyMaximum =
      baseAmountApplicableDays * dailyBenefitAmountBase + riderMaximumPayOut;

    return {
      maxPayoutAccordingToDailyMaximum,
      dailyBenefitAmountBase,
      baseBenefitAmountRemaining,
      baseAmountApplicableDays,
      inflatedFaceAmount,
      riderAmountApplicableDays,
      inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
      inflatedDailyBenefitAmountBase:
        inflatedDailyBenefitAmounts.policyInflationProtectionOnFaceAmount,
      inflatedDailyBenefitAmountRider:
        inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount,
    };
  }

  if (isHybridPolicy(ltcPolicy.policyType)) {
    dailyBenefitAmountBase = dailyBenefitAmount;

    // Cap base daily benefit amount by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountBase =
      inflatedDailyBenefitAmounts.policyInflationProtectionOnFaceAmount;
    const cappedDailyBenefitAmountBase = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountBase
      : Math.min(uncappedDailyBenefitAmountBase, phaseCostPerDayNeeded);

    // Cap rider daily benefit amount by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountRider =
      inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount;
    const cappedDailyBenefitAmountRider = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountRider
      : Math.min(uncappedDailyBenefitAmountRider, phaseCostPerDayNeeded);

    baseAmountApplicableDays = Math.min(
      baseBenefitAmountRemaining / cappedDailyBenefitAmountBase,
      phaseBenefitEligibleCareDays,
    );
    riderAmountApplicableDays = phaseBenefitEligibleCareDays - baseAmountApplicableDays;

    maxPayoutAccordingToDailyMaximum =
      baseAmountApplicableDays * cappedDailyBenefitAmountBase +
      riderAmountApplicableDays * cappedDailyBenefitAmountRider;

    return {
      maxPayoutAccordingToDailyMaximum,
      dailyBenefitAmountBase,
      baseBenefitAmountRemaining,
      baseAmountApplicableDays,
      riderAmountApplicableDays,
      inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
      inflatedDailyBenefitAmountBase: uncappedDailyBenefitAmountBase,
      inflatedDailyBenefitAmountRider: uncappedDailyBenefitAmountRider,
    };
  }

  maxPayoutAccordingToDailyMaximum =
    inflatedDailyBenefitAmounts.policyInflationProtection * phaseBenefitEligibleCareDays;

  return {
    maxPayoutAccordingToDailyMaximum,
    inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
  };
}

function handlePhaseBenefitEligibleCareDays(
  mutableClient: Draft<Client>,
  phaseCosts: SinglePhaseCosts,
) {
  const {
    intakeSurvey: { clientAdlReceivedAssistanceCount },
    fundingSources: {
      ltcPolicy: { policyBenefitPeriodMonthsRemaining, policyType },
      ltcPolicy,
    },
  } = mutableClient;
  const { phaseCareMonthsNeeded } = phaseCosts;

  const daysInMonth = 30;
  const oneAdlDurationMultiplier = 0.5;
  const phaseCareDaysNeeded = phaseCareMonthsNeeded * daysInMonth;

  if (policyType === FundingPolicyType.fortifiedLife) {
    return {
      phaseEligibleCareDays: phaseCareDaysNeeded,
      daysWhereLessThanTwoADLs: 0,
      applicableWaitingPeriodDays: 0,
      phaseEligibleCareDaysAfter2AdlRequirement: phaseCareDaysNeeded,
      phaseEligibleCareDaysAfterWaitingPeriod: phaseCareDaysNeeded,
    };
  }

  const policyBenefitPeriodDaysRemaining = (policyBenefitPeriodMonthsRemaining ?? 0) * daysInMonth;

  const policyActivatesAtADLCount = 2;

  const daysWhereLessThanTwoADLs =
    phaseCosts.carePhase === CarePhase.earlyCare &&
    clientAdlReceivedAssistanceCount < policyActivatesAtADLCount
      ? phaseCareDaysNeeded * oneAdlDurationMultiplier
      : 0;

  const phaseEligibleCareDaysAfter2AdlRequirement = phaseCareDaysNeeded - daysWhereLessThanTwoADLs;

  const applicableWaitingPeriodDays = calcApplicableWaitingPeriodDaysAndDecrement(
    mutableClient,
    phaseCosts,
    phaseEligibleCareDaysAfter2AdlRequirement,
  );

  if (checkPolicyCalculationTypeEquals(policyType, FundingPolicyType.shortTermCareInsurance)) {
    const phaseEligibleCareDays = Math.min(
      phaseCareDaysNeeded - (daysWhereLessThanTwoADLs + applicableWaitingPeriodDays),
      policyBenefitPeriodDaysRemaining,
    );
    ltcPolicy.policyBenefitPeriodMonthsRemaining! -= phaseEligibleCareDays / daysInMonth;

    const phaseEligibleCareDaysAfterWaitingPeriod = Math.min(
      phaseEligibleCareDays,
      phaseEligibleCareDaysAfter2AdlRequirement - applicableWaitingPeriodDays,
    );
    return {
      phaseEligibleCareDays,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }
  const phaseEligibleCareDays =
    phaseCareDaysNeeded - (daysWhereLessThanTwoADLs + applicableWaitingPeriodDays);

  const phaseEligibleCareDaysAfterWaitingPeriod =
    phaseEligibleCareDaysAfter2AdlRequirement - applicableWaitingPeriodDays;

  return {
    phaseEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
  };
}

function calcApplicableWaitingPeriodDaysAndDecrement(
  client: Draft<Client>,
  phaseCost: SinglePhaseCosts,
  phaseDaysNeeded: number,
): number {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: { homeCareWaitingPeriodDaysRemaining, facilityCareWaitingPeriodDaysRemaining },
    },
    careEnvironmentSelections,
    appliedCareEnvironments,
  } = client;

  const selectedCareSetting = careEnvironmentSelections?.[phaseCost.carePhase]
    ? careEnvironmentSelections[phaseCost.carePhase]
    : appliedCareEnvironments[phaseCost.carePhase];

  // calculate cost of waiting period based on care setting and corresponding waiting period
  if (selectedCareSetting === CareEnvironment.home && homeCareWaitingPeriodDaysRemaining) {
    const applicableWaitingDays = Math.min(homeCareWaitingPeriodDaysRemaining, phaseDaysNeeded);

    ltcPolicy.homeCareWaitingPeriodDaysRemaining! -= applicableWaitingDays;
    // Decrement remaining waiting period days for facility as well. Waiting periods are not independent
    if (facilityCareWaitingPeriodDaysRemaining) {
      ltcPolicy.facilityCareWaitingPeriodDaysRemaining = Math.max(
        0,
        facilityCareWaitingPeriodDaysRemaining - applicableWaitingDays,
      );
    }
    return applicableWaitingDays;
  }

  if (selectedCareSetting !== CareEnvironment.home && facilityCareWaitingPeriodDaysRemaining) {
    const applicableWaitingDays = Math.min(facilityCareWaitingPeriodDaysRemaining, phaseDaysNeeded);
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining! -= applicableWaitingDays;
    // Decrement remaining waiting period days for home as well. Waiting periods are not independent
    if (homeCareWaitingPeriodDaysRemaining) {
      ltcPolicy.homeCareWaitingPeriodDaysRemaining = Math.max(
        0,
        homeCareWaitingPeriodDaysRemaining - applicableWaitingDays,
      );
    }
    return applicableWaitingDays;
  }
  return 0;
}

export function setCoverageToUndefined<T extends keyof CalculatedPolicyFundingSource>(
  mutableClient: Draft<Client>,
  coverageType: T,
): void {
  mutableClient.fundingSources.ltcPolicy[coverageType] = undefined;
}

export function getAppliedCareEnvironmentKeys(mutableClient: Draft<Client>): string[] {
  const { appliedCareEnvironments } = mutableClient;

  return Object.values(appliedCareEnvironments)
    .map(value => getKeyByValue(CareEnvironment, value))
    .filter(key => key !== undefined) // Filter out undefined values
    .map(key => capitalizeFirstLetter(key!)); // Use non-null assertion operator
}

function sumPropertyForAllPolicies(
  property: keyof PolicyFundingSource,
  ltcPolicies: WritableDraft<PolicyFundingSource>[],
): number | undefined {
  // check if all values are undefined
  if (ltcPolicies.every(policy => policy[property] === undefined)) {
    return undefined;
  }
  return ltcPolicies.reduce((acc, policy) => {
    const value = policy[property];
    return acc + (typeof value === 'number' ? value : 0);
  }, 0);
}

export function calcCombinedFundingTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityTotalCost, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyTotalCost, multipleSelfFundingTotalCost },
  } = mutableClient;

  if (
    isNullOrUndefined(multipleSelfFundingTotalCost) &&
    isNullOrUndefined(multiplePolicyTotalCost) &&
    isNullOrUndefined(annuityTotalCost)
  ) {
    fundingSources.combinedFundingTotalCost = undefined;
    return;
  }

  fundingSources.combinedFundingTotalCost =
    (multipleSelfFundingTotalCost ?? 0) +
    (multiplePolicyTotalCost ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityTotalCost ?? 0));
}

function calcCombinedNonLtcPayout(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityNonLtcPayout, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyNonLtcPayout, multipleSelfFundingNonLtcPayout },
  } = mutableClient;

  fundingSources.combinedNonLtcPayout =
    (multipleSelfFundingNonLtcPayout ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityNonLtcPayout ?? 0)) +
    (multiplePolicyNonLtcPayout ?? 0);
}

export function roundDownToNearestHundredth(value: number): number {
  return Math.floor(value * 100) / 100;
}

export function calcCombinedProjectedCostCoverage(mutableClient: Draft<Client>) {
  const {
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    fundingSources,
    fundingSources: {
      annuity: { annuityProjectedCostCoverage, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: {
      multiplePolicyProjectedCostCoverage,
      multipleSelfFundingProjectedCostCoverage,
    },
  } = mutableClient;

  if (
    isNullOrUndefined(multipleSelfFundingProjectedCostCoverage) &&
    isNullOrUndefined(multiplePolicyProjectedCostCoverage) &&
    isNullOrUndefined(allPhaseInflatedProfessionalShareCost)
  ) {
    fundingSources.combinedProjectedCostCoverage = undefined;
    fundingSources.combinedProjectedCostCoveragePercent = undefined;
    return;
  }

  fundingSources.combinedProjectedCostCoverage =
    (multipleSelfFundingProjectedCostCoverage ?? 0) +
    (multiplePolicyProjectedCostCoverage ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityProjectedCostCoverage ?? 0));

  fundingSources.combinedProjectedCostCoveragePercent =
    allPhaseInflatedProfessionalShareCost > 0
      ? roundDownToNearestHundredth(
          (fundingSources.combinedProjectedCostCoverage ?? 0) /
            allPhaseInflatedProfessionalShareCost,
        )
      : 1;
}

export function calcPolicyInflatedMaxBenefitToEndOfPhase(
  client: Client,
  carePhase: CarePhase,
  policyPremiumStartYear: number,
  policyMaximumBenefitAmount: number,
  inflationRate?: number,
  policyInflationProtectionYears?: number | null,
) {
  const {
    clientPhasePredictedEndYears,
    fundingSources: {},
  } = client;

  if (isCarePhasePast(carePhase, client)) {
    return policyMaximumBenefitAmount;
  }

  let inflationPeriod = Math.max(
    clientPhasePredictedEndYears[carePhase]! - policyPremiumStartYear,
    0,
  );
  // Limit inflation period by policyInflationProtectionYears if it exists
  if (!isNullOrUndefined(policyInflationProtectionYears)) {
    inflationPeriod = Math.min(inflationPeriod, policyInflationProtectionYears);
  }

  client.fundingSources.ltcPolicy.policyInflationPeriodYears = inflationPeriod;
  return calcInflationProtectionFutureValue(
    client,
    policyMaximumBenefitAmount,
    inflationPeriod,
    inflationRate,
  );
}

function calcInflationProtectionFutureValue(
  client: Client,
  principle: number,
  inflationPeriodYears: number,
  inflationRate?: number,
) {
  const {
    fundingSources: {
      ltcPolicy: { policyInflationProtection, isSimpleInflationProtection },
    },
  } = client;
  const effectiveInflationRate = inflationRate ?? policyInflationProtection;
  return isSimpleInflationProtection
    ? calcFutureValueSimpleInflation(principle, inflationPeriodYears, effectiveInflationRate ?? 0)
    : calcFutureValue(principle, effectiveInflationRate ?? 0, inflationPeriodYears);
}

export function calcPolicyCoverageImpl(mutableClient: Draft<Client>, coverageType: string): void {
  const {
    fundingSources: {
      ltcPolicy: { policyType },
      ltcPolicy,
    },
    phaseCosts,
    mutableClientPartner,
  } = mutableClient;

  let policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator = {
    indemnitySurplus: 0,
    phaseCalculations: [],
    projectedCostCoverage: 0,
    cumulativePolicyPayOut: 0, // may be different than projected cost coverage for indemnity policies
    benefitPoolsExhausted: {},
    sharedPoolUtilized: 0,
  };

  if (isJointPolicy(policyType!, mutableClientPartner) && !ltcPolicy.isDeactivated) {
    copyJointPolicyToClientPartner(mutableClient);
    const combinedSortedPhaseCosts = combineSortClientPartnerPhaseCosts(mutableClient);

    policyDataAccumulator = {
      ...policyDataAccumulator,
      partnerPhaseCalculations: [],
      partnerProjectedCostCoverage: 0,
    };

    combinedSortedPhaseCosts.forEach(clientSinglePhaseCost => {
      processSinglePhase<JointPolicyDataAccumulator>(
        mutableClient.clientId,
        clientSinglePhaseCost.client,
        policyDataAccumulator as JointPolicyDataAccumulator,
        clientSinglePhaseCost.phaseCosts,
      );
    });
  } else {
    phaseCosts.forEach(singePhaseCosts => {
      processSinglePhase<PolicyDataAccumulator>(
        mutableClient.clientId,
        mutableClient,
        policyDataAccumulator,
        singePhaseCosts,
      );
    });
  }

  calcPolicySummaryStats(mutableClient, policyDataAccumulator);

  ltcPolicy[coverageType] = policyDataAccumulator.projectedCostCoverage;
  const lastPhaseCalculations =
    policyDataAccumulator.phaseCalculations[policyDataAccumulator.phaseCalculations.length - 1];
  ltcPolicy.policyRemainingBenefitAmount =
    lastPhaseCalculations.remainingMaxBenefitAfterPhaseReduction ?? 0;
  ltcPolicy.indemnitySurplus = policyDataAccumulator.indemnitySurplus;
  mutableClient.phaseCalculations = policyDataAccumulator.phaseCalculations;

  if (
    isJointPolicy(policyType!, mutableClientPartner) &&
    isJointPolicyDataAccumulator(policyDataAccumulator)
  ) {
    mutableClientPartner.phaseCalculations = policyDataAccumulator.partnerPhaseCalculations;
    mutableClientPartner.fundingSources.ltcPolicy[coverageType] =
      policyDataAccumulator.partnerProjectedCostCoverage;
  }
}

export function isJointPolicy(
  policyType: FundingPolicyType | null,
  mutableClientPartner: Client | null,
): mutableClientPartner is NonNullable<Client> {
  if (!policyType || !mutableClientPartner) {
    return false;
  }
  return fundingPolicyTypeDefs[policyType].isJoint === true;
}

type BenefitPoolKey =
  | 'totalBenefitAmount'
  | 'clientIndividualPoolAmount'
  | 'partnerIndividualPoolAmount'
  | 'sharedPoolAmount';
type PolicyDataAccumulatorBase = {
  indemnitySurplus: number;
  phaseCalculations: SinglePhaseCalculations[];
  projectedCostCoverage: number;
  cumulativePolicyPayOut: number;
  benefitPoolsExhausted: {
    [key in BenefitPoolKey]?: boolean;
  };
};

type PolicyDataAccumulator = PolicyDataAccumulatorBase;

type JointPolicyDataAccumulator = PolicyDataAccumulatorBase & {
  partnerPhaseCalculations: SinglePhaseCalculations[];
  partnerProjectedCostCoverage: number;
  sharedPoolUtilized: number;
};

function isJointPolicyDataAccumulator(
  accumulator: PolicyDataAccumulatorBase,
): accumulator is JointPolicyDataAccumulator {
  return (accumulator as JointPolicyDataAccumulator).partnerPhaseCalculations !== undefined;
}

function calcRemainingBenefitForShareCareRiderPlans(
  inflatedIndividualPoolAmount: number,
  policyDataAccumulator: JointPolicyDataAccumulator | PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  policySharedBenefitPoolRemaining: number,
) {
  const { projectedCostCoverage, benefitPoolsExhausted } = policyDataAccumulator;

  // Get partner coverage amount if joint policy, otherwise 0
  const partnerProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? policyDataAccumulator.partnerProjectedCostCoverage
    : 0;

  // Determine if individual pool is exhausted based on client type
  const isIndividualPoolExhausted = isClientSessionClient
    ? benefitPoolsExhausted.clientIndividualPoolAmount
    : benefitPoolsExhausted.partnerIndividualPoolAmount;

  // Get coverage amount based on client type
  const coverageAmount = isClientSessionClient
    ? projectedCostCoverage
    : partnerProjectedCostCoverage;

  // Calculate remaining individual pool amount
  const individualPoolRemaining = isIndividualPoolExhausted
    ? 0
    : Math.max(inflatedIndividualPoolAmount - coverageAmount, 0);

  // Return total remaining benefit including shared pool
  return Math.max(individualPoolRemaining + policySharedBenefitPoolRemaining, 0);
}

function processSinglePhase<AccumlatorType extends PolicyDataAccumulatorBase>(
  sessionClientId: string,
  mutableClient: Draft<Client>,
  policyDataAccumulator: AccumlatorType,
  phaseCosts: SinglePhaseCosts,
): void {
  const {
    fundingSources: {
      ltcPolicy: {
        isIndemnityPolicyPayment,
        policyPremiumStartYear,
        policyType,
        policyMaximumBenefitAmount,
        policyInflationProtection,
        policyBenefitUtilizedToDate,
      },
      ltcPolicy,
    },
  } = mutableClient;

  const policyTypeHasSharedPool =
    (policyType === FundingPolicyType.nylJointSecureCare ||
      policyType === FundingPolicyType.jointNglEssentialLtc) &&
    isJointPolicyDataAccumulator(policyDataAccumulator);

  // is the client being processed the session client (if false, it is the parter client)
  const isClientSessionClient = sessionClientId === mutableClient.clientId;

  let { indemnitySurplus, cumulativePolicyPayOut } = policyDataAccumulator;
  if (isNullOrUndefined(policyPremiumStartYear)) {
    throw new Error('Policy premium start year is not defined');
  }

  let inflatedMaxBenefitAmount;
  let remainingMaxBenefitBeforePhaseReduction;

  if (policyDataAccumulator.benefitPoolsExhausted.totalBenefitAmount) {
    // If benefit pool is exhausted
    inflatedMaxBenefitAmount = 0;
    remainingMaxBenefitBeforePhaseReduction = 0;

    const previousPhase =
      phaseCosts.carePhase === CarePhase.earlyCare ? CarePhase.earlyCare : phaseCosts.carePhase - 1;
    ltcPolicy.policyTotalLTCBenefit = calcPolicyTotalLtcLimitForPhase(
      mutableClient,
      previousPhase,
      unlimitedMaxBenefitMagicNumber,
      policyPremiumStartYear,
    );
  } else {
    inflatedMaxBenefitAmount = calcPolicyTotalLtcLimitForPhase(
      mutableClient,
      phaseCosts.carePhase,
      unlimitedMaxBenefitMagicNumber,
      policyPremiumStartYear,
    );

    if (phaseCosts.carePhase === CarePhase.fullCare) {
      ltcPolicy.policyTotalLTCBenefit = inflatedMaxBenefitAmount;
    }

    remainingMaxBenefitBeforePhaseReduction =
      inflatedMaxBenefitAmount -
      cumulativePolicyPayOut -
      (ltcPolicy.policyBenefitUtilizedToDate ?? 0);
  }

  let policySharedBenefitPoolRemaining;

  if (policyTypeHasSharedPool) {
    if (
      policyDataAccumulator.benefitPoolsExhausted.sharedPoolAmount &&
      policyDataAccumulator.benefitPoolsExhausted.clientIndividualPoolAmount &&
      policyDataAccumulator.benefitPoolsExhausted.partnerIndividualPoolAmount
    ) {
      policySharedBenefitPoolRemaining = 0;
      remainingMaxBenefitBeforePhaseReduction = 0;
      inflatedMaxBenefitAmount = 0;
    } else {
      const individualPoolAmount =
        ((policyMaximumBenefitAmount ?? 0) - (policyBenefitUtilizedToDate ?? 0)) / 3;

      ltcPolicy.policySharedBenefitPool = individualPoolAmount;

      const inflatedIndividualPoolAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
        mutableClient,
        phaseCosts.carePhase,
        policyPremiumStartYear,
        individualPoolAmount,
        policyInflationProtection ?? 0,
      );

      // total ltc benefit amount is 2/3 of the fully inflated max benefit amount
      // this accounts for the individual benefit pool and the shared benefit pool
      inflatedMaxBenefitAmount = inflatedIndividualPoolAmount;

      policySharedBenefitPoolRemaining = policyDataAccumulator.benefitPoolsExhausted
        .sharedPoolAmount
        ? 0
        : Math.max(
            inflatedIndividualPoolAmount - (policyDataAccumulator.sharedPoolUtilized ?? 0),
            0,
          );

      // calculate remaining shared pool
      remainingMaxBenefitBeforePhaseReduction = calcRemainingBenefitForShareCareRiderPlans(
        inflatedIndividualPoolAmount,
        policyDataAccumulator,
        isClientSessionClient,
        policySharedBenefitPoolRemaining,
      );
    }
  }

  const phaseIndex = carePhaseDefs[phaseCosts.carePhase].index;

  let costCoverageAddedToTotal;
  const phaseHomeCareWaitingPeriodDaysRemaining = ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? 0;
  const phaseFacilityCareWaitingPeriodDaysRemaining =
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining ?? 0;

  const {
    portionOfPhaseCostBenefitEligible,
    maxPayoutAccordingToDailyMaximum,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    phaseCareDaysNeeded,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
    dailyBenefitAmount,
    inflationProtection,
    inflationPeriodYears,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
  } = calcSinglePhasePolicyCoverage(
    mutableClient,
    phaseCosts.appliedCareEnvironment,
    phaseCosts,
    policyDataAccumulator,
  );

  // coverage for phase cost is capped by whichever is less
  // 1) the portion of the phase cost that is benefit eligible
  // 2) the remaining max benefit
  // 3) the max payout according to daily maximum, if applicable
  const phaseCostCoverage = Math.min(
    portionOfPhaseCostBenefitEligible,
    remainingMaxBenefitBeforePhaseReduction,
    maxPayoutAccordingToDailyMaximum ?? Infinity,
  );
  let remainingMaxBenefitAfterPhaseReduction;

  // handle updates for reimbursement policy
  const isReimbursementPolicyPayment = !isIndemnityPolicyPayment;
  if (isReimbursementPolicyPayment) {
    // handle reimbursement policy
    costCoverageAddedToTotal = phaseCostCoverage;

    updateProjectedCostCoverage(policyDataAccumulator, isClientSessionClient, phaseCostCoverage);
    // update remaining max benefit
    policyDataAccumulator.cumulativePolicyPayOut += phaseCostCoverage;
    remainingMaxBenefitAfterPhaseReduction = Math.max(
      remainingMaxBenefitBeforePhaseReduction - phaseCostCoverage,
      0,
    );
  }

  let surplusAppliedToCoverage;
  let indemnitySurplusForPhase;
  let indemnityPayout;
  let sharedPoolUtilized;

  // handle updates for indemnity policy
  if (isIndemnityPolicyPayment) {
    // calculate indemnity payout
    indemnityPayout = maxPayoutAccordingToDailyMaximum
      ? Math.min(maxPayoutAccordingToDailyMaximum, remainingMaxBenefitBeforePhaseReduction)
      : phaseCostCoverage; // there should always be daily max, but just in case

    // handle coverage update when there is surplus (funds leftover from previous phases)
    if (indemnitySurplus > 0 && phaseCostCoverage < portionOfPhaseCostBenefitEligible) {
      // if coverage is less than the benefit eligible cost, apply surplus to coverage
      const remainingCostToCover =
        phaseCosts.phaseInflatedProfessionalShareCost - phaseCostCoverage;
      surplusAppliedToCoverage = Math.min(indemnitySurplus, remainingCostToCover);
      costCoverageAddedToTotal = phaseCostCoverage + surplusAppliedToCoverage;
      updateProjectedCostCoverage(
        policyDataAccumulator,
        isClientSessionClient,
        costCoverageAddedToTotal,
      );
      policyDataAccumulator.indemnitySurplus -= surplusAppliedToCoverage;
    } else {
      // if no surplus or phase cost is greater than the benefit eligible cost, update coverage normally
      costCoverageAddedToTotal = phaseCostCoverage;
      updateProjectedCostCoverage(
        policyDataAccumulator,
        isClientSessionClient,
        costCoverageAddedToTotal,
      );
      indemnitySurplusForPhase = Math.max(indemnityPayout - phaseCostCoverage, 0); // should not be negative but just in case
      policyDataAccumulator.indemnitySurplus += indemnitySurplusForPhase;
    }
    policyDataAccumulator.cumulativePolicyPayOut += indemnityPayout;
    remainingMaxBenefitAfterPhaseReduction = Math.max(
      remainingMaxBenefitBeforePhaseReduction - indemnityPayout,
      0,
    );
  }

  if (policyTypeHasSharedPool) {
    sharedPoolUtilized = updateBenefitPoolExhaustedStatus(
      policyDataAccumulator as JointPolicyDataAccumulator,
      isClientSessionClient,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  }

  // update phase calculations for whitebox explanations
  const phaseCalculations = {
    phase: phaseCosts.carePhase,
    phaseMonthlyCost: phaseMonthlyCost ?? 0,
    phaseCostPerDayNeeded: phaseCostPerDayNeeded ?? 0,
    phaseCareDaysNeeded: phaseCareDaysNeeded ?? 0,
    daysWhereLessThanTwoADLs: daysWhereLessThanTwoADLs ?? 0,
    applicableWaitingPeriodDays: applicableWaitingPeriodDays ?? 0,
    phaseEligibleCareDaysAfter2AdlRequirement: phaseEligibleCareDaysAfter2AdlRequirement ?? 0,
    phaseEligibleCareDaysAfterWaitingPeriod: phaseEligibleCareDaysAfterWaitingPeriod ?? 0,
    phaseFacilityCareWaitingPeriodDaysRemaining: phaseFacilityCareWaitingPeriodDaysRemaining ?? 0,
    phaseHomeCareWaitingPeriodDaysRemaining: phaseHomeCareWaitingPeriodDaysRemaining ?? 0,
    dailyBenefitAmount: dailyBenefitAmount ?? 0,
    inflationProtection: inflationProtection ?? 0,
    inflationPeriodYears: inflationPeriodYears ?? 0,
    inflatedDailyBenefitAmount: inflatedDailyBenefitAmount ?? 0,
    inflatedMaxBenefitAmount: inflatedMaxBenefitAmount ?? 0,
    maxPayoutAccordingToDailyMaximum: maxPayoutAccordingToDailyMaximum ?? 0,
    surplusAppliedToCoverage: surplusAppliedToCoverage ?? 0,
    indemnitySurplusForPhase: indemnitySurplusForPhase ?? 0,
    phaseCostCoverage: phaseCostCoverage ?? 0,
    costCoverageAddedToTotal: costCoverageAddedToTotal ?? 0,
    portionOfPhaseCostBenefitEligible: portionOfPhaseCostBenefitEligible ?? 0,
    remainingMaxBenefitBeforePhaseReduction: remainingMaxBenefitBeforePhaseReduction ?? 0,
    remainingMaxBenefitAfterPhaseReduction: remainingMaxBenefitAfterPhaseReduction ?? 0,
    cumulativePolicyPayOut: cumulativePolicyPayOut ?? 0,
    indemnityPayout: indemnityPayout ?? 0,
    dailyBenefitAmountBase: dailyBenefitAmountBase ?? 0,
    baseBenefitAmountRemaining: baseBenefitAmountRemaining ?? 0,
    inflatedFaceAmount: inflatedFaceAmount ?? 0,
    baseAmountApplicableDays: baseAmountApplicableDays ?? 0,
    riderAmountApplicableDays: riderAmountApplicableDays ?? 0,
    indemnitySurplusBeforePhaseReduction: indemnitySurplus ?? 0,
    policySharedBenefitPoolRemaining: policySharedBenefitPoolRemaining ?? null,
    inflatedDailyBenefitAmountBase: inflatedDailyBenefitAmountBase ?? 0,
    inflatedDailyBenefitAmountRider: inflatedDailyBenefitAmountRider ?? 0,
    sharedPoolUtilized: sharedPoolUtilized ?? 0,
  };

  updatePhaseCalculations(
    policyDataAccumulator,
    isClientSessionClient,
    phaseIndex,
    phaseCalculations,
  );
}

function updateBenefitPoolExhaustedStatus(
  policyDataAccumulator: JointPolicyDataAccumulator,
  isClientSessionClient: boolean,
  inflatedMaxBenefitAmount: number,
  costCoverageAddedToTotal: number | undefined,
): number | undefined {
  const { projectedCostCoverage, partnerProjectedCostCoverage } = policyDataAccumulator;

  let sharedPoolUtilized: number | undefined;

  if (isClientSessionClient) {
    sharedPoolUtilized = updateClientPoolStatus(
      policyDataAccumulator,
      projectedCostCoverage,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  } else {
    sharedPoolUtilized = updatePartnerPoolStatus(
      policyDataAccumulator,
      partnerProjectedCostCoverage,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  }

  updateSharedPoolStatus(policyDataAccumulator, inflatedMaxBenefitAmount);

  return sharedPoolUtilized;
}

function updateClientPoolStatus(
  accumulator: JointPolicyDataAccumulator,
  projectedCoverage: number,
  maxBenefit: number,
  costCoverageAdded: number | undefined,
): number | undefined {
  let sharedPoolUtilized: number | undefined;

  // Handle already exhausted pool
  if (accumulator.benefitPoolsExhausted.clientIndividualPoolAmount) {
    sharedPoolUtilized = accumulator.sharedPoolUtilized + (costCoverageAdded ?? 0);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
    return sharedPoolUtilized;
  }

  // Check if pool becomes exhausted
  if (projectedCoverage >= maxBenefit) {
    accumulator.benefitPoolsExhausted.clientIndividualPoolAmount = true;
    sharedPoolUtilized =
      accumulator.sharedPoolUtilized + Math.max(0, projectedCoverage - maxBenefit);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
  }

  return sharedPoolUtilized;
}

function updatePartnerPoolStatus(
  accumulator: JointPolicyDataAccumulator,
  projectedCoverage: number,
  maxBenefit: number,
  costCoverageAdded: number | undefined,
): number | undefined {
  let sharedPoolUtilized: number | undefined;

  // Handle already exhausted pool
  if (accumulator.benefitPoolsExhausted.partnerIndividualPoolAmount) {
    sharedPoolUtilized = accumulator.sharedPoolUtilized + (costCoverageAdded ?? 0);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
    return sharedPoolUtilized;
  }

  // Check if pool becomes exhausted
  if (projectedCoverage >= maxBenefit) {
    accumulator.benefitPoolsExhausted.partnerIndividualPoolAmount = true;
    sharedPoolUtilized =
      accumulator.sharedPoolUtilized + Math.max(0, projectedCoverage - maxBenefit);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
  }

  return sharedPoolUtilized;
}

function updateSharedPoolStatus(accumulator: JointPolicyDataAccumulator, maxBenefit: number) {
  if (
    !accumulator.benefitPoolsExhausted.sharedPoolAmount &&
    accumulator.sharedPoolUtilized &&
    accumulator.sharedPoolUtilized >= maxBenefit
  ) {
    accumulator.benefitPoolsExhausted.sharedPoolAmount = true;
  }
}

function updateProjectedCostCoverage(
  policyDataAccumulator: PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  coverageToAdd: number,
): void {
  if (isClientSessionClient) {
    policyDataAccumulator.projectedCostCoverage += coverageToAdd;
  } else if (isJointPolicyDataAccumulator(policyDataAccumulator)) {
    policyDataAccumulator.partnerProjectedCostCoverage += coverageToAdd;
  } else {
    throw new Error('Accumulator is not a JointPolicyDataAccumulator when expected');
  }
}

function updatePhaseCalculations(
  policyDataAccumulator: PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  phaseIndex: number,
  phaseCalculations: SinglePhaseCalculations,
): void {
  if (isClientSessionClient) {
    policyDataAccumulator.phaseCalculations[phaseIndex] = phaseCalculations;
  } else if (isJointPolicyDataAccumulator(policyDataAccumulator)) {
    policyDataAccumulator.partnerPhaseCalculations[phaseIndex] = phaseCalculations;
  } else {
    throw new Error('Accumulator is not a JointPolicyDataAccumulator when expected');
  }
}

function calcHybridPolicyTotalValue({
  ltcPolicy,
  totalProjectedCostCoverage,
  policyInflatedDeathBenefitAmount,
  indemnitySurplus,
}: {
  ltcPolicy: PolicyFundingSource;
  totalProjectedCostCoverage: number;
  policyInflatedDeathBenefitAmount: number;
  indemnitySurplus: number;
}): number {
  const {
    policyType,
    policyMaximumBenefitAmount,
    policyBenefitIsUnlimited,
    policyMinimumGuaranteedBenefitAmount,
  } = ltcPolicy;

  if (policyBenefitIsUnlimited) {
    const remainingDeathBenefitAmount =
      policyInflatedDeathBenefitAmount - totalProjectedCostCoverage;

    return (
      totalProjectedCostCoverage +
      Math.max(remainingDeathBenefitAmount, policyMinimumGuaranteedBenefitAmount ?? 0, 0) +
      indemnitySurplus
    );
  }

  // Coverage is greater than death benefit
  const dontInflateDeathBenefit =
    policyType === FundingPolicyType.careMattersTwo ||
    policyType === FundingPolicyType.jointCareMatters ||
    policyType === FundingPolicyType.allstateHybridPolicy;
  const deathBenefit = dontInflateDeathBenefit
    ? (policyMaximumBenefitAmount ?? 0)
    : policyInflatedDeathBenefitAmount;

  if (totalProjectedCostCoverage >= deathBenefit) {
    return (
      totalProjectedCostCoverage + (policyMinimumGuaranteedBenefitAmount ?? 0) + indemnitySurplus
    );
  }

  // Coverage is less than death benefit amount
  const policyCoveragePlusIndemnitySurplus = totalProjectedCostCoverage + indemnitySurplus;

  return (
    totalProjectedCostCoverage +
    Math.max(
      policyMinimumGuaranteedBenefitAmount ?? 0,
      deathBenefit - policyCoveragePlusIndemnitySurplus,
      0,
    ) +
    indemnitySurplus
  );
}

export function calcPolicySummaryStats(
  mutableClient: Draft<Client>,
  policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator,
) {
  const {
    fundingSources: {
      ltcPolicy: {
        policyType,
        isIndemnityPolicyPayment,
        policyPremiumStartYear,
        policyMaximumBenefitAmount,
        policyInflationProtectionOnFaceAmount,
        policyInflationProtection,
        policyInflationProtectionYears,
      },
      ltcPolicy,
    },
  } = mutableClient;

  const { projectedCostCoverage, indemnitySurplus } = policyDataAccumulator;
  const totalProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? projectedCostCoverage + policyDataAccumulator.partnerProjectedCostCoverage
    : projectedCostCoverage;

  // For now, only traditional ltci, stci, annuity hybrids, the total value is the projected coverage amount
  if (
    checkPolicyCalculationTypeEquals(policyType, [
      FundingPolicyType.longTermCareInsurance,
      FundingPolicyType.shortTermCareInsurance,
      FundingPolicyType.annuityHybrid,
    ])
  ) {
    ltcPolicy.policyNonLtcPayout = isIndemnityPolicyPayment ? indemnitySurplus : 0;
    ltcPolicy.policyTotalValue =
      totalProjectedCostCoverage + (isIndemnityPolicyPayment ? indemnitySurplus : 0);
  }

  // For other policy types, the policy total value is the maximum benefit amount assuming that
  // whatever is leftover will be paid out in some form of death benefit to beneficiaries
  if (
    checkPolicyCalculationTypeEquals(policyType, [
      FundingPolicyType.hybridLifeInsurance,
      FundingPolicyType.lifeInsuranceWithRider,
      FundingPolicyType.hybridLifeIra,
    ])
  ) {
    const policyInflatedDeathBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
      mutableClient,
      CarePhase.fullCare,
      policyPremiumStartYear!,
      policyMaximumBenefitAmount ?? 0,
      policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? undefined,
      policyInflationProtectionYears,
    );

    ltcPolicy.policyInflatedDeathBenefitAmount = policyInflatedDeathBenefitAmount;

    // Calculate policy total value using the extracted function
    ltcPolicy.policyTotalValue = calcHybridPolicyTotalValue({
      ltcPolicy,
      policyInflatedDeathBenefitAmount,
      totalProjectedCostCoverage,
      indemnitySurplus,
    });

    // Calculate non ltc payout.
    // Simplifying assumption: assuming that this is the max benefit minus the projected coverage for hybrid and life with rider
    ltcPolicy.policyNonLtcPayout = (ltcPolicy.policyTotalValue ?? 0) - totalProjectedCostCoverage;
  }
}

// Helper types to make the code more readable
type PolicyInflationParams = {
  amount: number;
  inflationProtection: number;
};

function calcPolicyTotalLtcLimitForPhase(
  mutableClient: Draft<Client>,
  carePhase: CarePhase,
  unlimitedMaxBenefitMagicNumber: number,
  policyPremiumStartYear: number,
) {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyMaximumBenefitAmount,
        policyContinuationBenefitAmount,
        policyType,
        policyInflationProtection,
        policyInflationProtectionOnCOBAmount,
        policyInflationProtectionOnFaceAmount,
        policyInflationProtectionYears,
      },
    },
  } = mutableClient;

  // Early return for unlimited hybrid policies
  if (isHybridPolicy(policyType) && isUnlimitedBenefit(policyContinuationBenefitAmount)) {
    return handleUnlimitedBenefit(ltcPolicy, unlimitedMaxBenefitMagicNumber);
  }

  // Early return for unlimited standard policies
  if (!isHybridPolicy(policyType) && isUnlimitedBenefit(policyMaximumBenefitAmount)) {
    return handleUnlimitedBenefit(ltcPolicy, unlimitedMaxBenefitMagicNumber);
  }

  // Handle hybrid policy calculations
  if (isHybridPolicy(policyType)) {
    return calculateHybridPolicyBenefit({
      client: mutableClient,
      carePhase,
      policyPremiumStartYear,
      continuationBenefit: {
        amount: policyContinuationBenefitAmount!,
        inflationProtection: policyInflationProtectionOnCOBAmount ?? policyInflationProtection ?? 0,
      },
      deathBenefit: {
        amount: policyMaximumBenefitAmount ?? 0,
        inflationProtection:
          policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? 0,
      },
      policyInflationProtectionYears,
    });
  }

  // Handle standard policy calculations
  return calculateStandardPolicyBenefit({
    client: mutableClient,
    carePhase,
    policyPremiumStartYear,
    maximumBenefit: policyMaximumBenefitAmount!,
    hasInflationProtection: !!policyInflationProtection,
    policyInflationProtectionYears,
  });
}

// Helper functions
function isHybridPolicy(policyType: FundingPolicyType | null): boolean {
  return checkPolicyCalculationTypeEquals(policyType, [
    FundingPolicyType.hybridLifeInsurance,
    FundingPolicyType.hybridLifeIra,
  ]);
}

function isUnlimitedBenefit(amount: number | undefined | null): boolean {
  return isNullOrUndefined(amount);
}

function handleUnlimitedBenefit(
  ltcPolicy: Draft<PolicyFundingSource>,
  unlimitedMaxBenefitMagicNumber: number,
): number {
  ltcPolicy.policyBenefitIsUnlimited = true;
  return unlimitedMaxBenefitMagicNumber;
}

type HybridPolicyParams = {
  client: Draft<Client>;
  carePhase: CarePhase;
  policyPremiumStartYear: number;
  continuationBenefit: PolicyInflationParams;
  deathBenefit: PolicyInflationParams;
  policyInflationProtectionYears?: number | null;
};

function calculateHybridPolicyBenefit({
  client,
  carePhase,
  policyPremiumStartYear,
  continuationBenefit,
  deathBenefit,
  policyInflationProtectionYears,
}: HybridPolicyParams): number {
  const inflatedContinuationBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    continuationBenefit.amount,
    continuationBenefit.inflationProtection,
    policyInflationProtectionYears,
  );

  const inflatedDeathBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    deathBenefit.amount,
    deathBenefit.inflationProtection,
    policyInflationProtectionYears,
  );

  return inflatedContinuationBenefitAmount + inflatedDeathBenefitAmount;
}

type StandardPolicyParams = {
  client: Draft<Client>;
  carePhase: CarePhase;
  policyPremiumStartYear: number;
  maximumBenefit: number;
  hasInflationProtection: boolean;
  policyInflationProtectionYears?: number | null;
};

function calculateStandardPolicyBenefit({
  client,
  carePhase,
  policyPremiumStartYear,
  maximumBenefit,
  hasInflationProtection,
  policyInflationProtectionYears,
}: StandardPolicyParams): number {
  if (!hasInflationProtection) {
    return maximumBenefit;
  }

  return calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    maximumBenefit,
    undefined,
    policyInflationProtectionYears,
  );
}

export function initializeRemainingFields(mutableClient: Draft<Client>) {
  const {
    fundingSources: { ltcPolicy },
  } = mutableClient;

  ltcPolicy.homeCareWaitingPeriodDaysRemaining =
    ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays;
  ltcPolicy.facilityCareWaitingPeriodDaysRemaining =
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining ?? ltcPolicy.facilityCareWaitingPeriodDays;
  ltcPolicy.policyBenefitPeriodMonthsRemaining =
    ltcPolicy.policyBenefitPeriodMonthsRemaining ?? ltcPolicy.policyBenefitPeriodMonths;
}

export function calcCombinedTotalValue(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityTotalValue, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyTotalValue, multipleSelfFundingTotalValue },
  } = mutableClient;

  fundingSources.combinedTotalValue =
    (multipleSelfFundingTotalValue ?? 0) +
    (multiplePolicyTotalValue ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityTotalValue ?? 0));
}

function addDiscountToReturnCashFlows(
  baseReturnCashFlows: CashFlow[],
  discountAmount: number,
): CashFlow[] {
  if (discountAmount <= 0 || baseReturnCashFlows.length === 0) {
    return baseReturnCashFlows;
  }

  // Distribute discount evenly across all return cash flows
  const discountPerCashFlow = discountAmount / baseReturnCashFlows.length;

  return baseReturnCashFlows.map(cashFlow => ({
    ...cashFlow,
    amount: cashFlow.amount + discountPerCashFlow,
  }));
}

export function calcPolicyProjectedROI(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  discountRate?: number,
): ReturnOnInvestmentCalculations & {
  policyProjectedReturnOnInvestment?: number;
  policyProjectedCompoundAnnualGrowthRate?: number;
  policyProjectedInternalRateOfReturn?: number;
} {
  const {
    intakeSurvey: { clientHasStartedLtc },
    clientPhasePredictedEndYears,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
  } = mutableClient;

  const {
    policyTotalCost,
    policyCoverageOfRemainingLtcCosts,
    projectedCostCoverage,
    policyPremiumStartYear,
  } = ltcPolicy;

  const coverageAmount = clientHasStartedLtc
    ? policyCoverageOfRemainingLtcCosts
    : projectedCostCoverage;

  let predictedEndYear = clientPhasePredictedEndYears[CarePhase.fullCare];

  if (
    isNullOrUndefined(coverageAmount) ||
    isNullOrUndefined(policyTotalCost) ||
    isNullOrUndefined(predictedEndYear) ||
    isNullOrUndefined(policyPremiumStartYear)
  ) {
    return {
      investments: [],
      returns: [],
      investmentPeriodYears: 0,
    };
  }

  // If the policy is joint, use the max predicted end year
  if (
    ltcPolicy.policyType &&
    fundingPolicyTypeDefs[ltcPolicy.policyType].isJoint &&
    mutableClient.mutableClientPartner
  ) {
    predictedEndYear = Math.max(
      predictedEndYear,
      mutableClient.mutableClientPartner.clientPhasePredictedEndYears[CarePhase.fullCare] ?? 0,
    );
  }

  // Convert premiums and returns to cash flows
  const investmentCashFlows = convertPolicyPremiumsToCashFlows(ltcPolicy);
  const baseReturnCashFlows = convertPolicyReturnCashFlow(mutableClient);
  const returnCashFlows = addDiscountToReturnCashFlows(
    baseReturnCashFlows,
    allPhaseInflatedProfessionalShareCostDiscountAmount,
  );

  // Analyze the investment returns
  const { totalReturns, cagr, internalRateOfReturn, yearsToLastReturn } = analyzeInvestmentReturns(
    investmentCashFlows,
    returnCashFlows,
    discountRate,
    policyPremiumStartYear,
  );

  return {
    investments: investmentCashFlows,
    returns: returnCashFlows,
    investmentPeriodYears: yearsToLastReturn,
    policyProjectedReturnOnInvestment: (totalReturns - policyTotalCost) / policyTotalCost,
    policyProjectedCompoundAnnualGrowthRate: cagr,
    policyProjectedInternalRateOfReturn: internalRateOfReturn,
  };
}

function investmentCashflowEqualsPolicyTotalCost(
  investmentCashFlows: CashFlow[],
  policyTotalCost: number,
) {
  // test that total cash flows are equal to the policy total cost
  const totalInvestmentCashFlows = investmentCashFlows.reduce((sum, cf) => sum + cf.amount, 0);

  // Calculate percentage difference
  const percentDifference = Math.abs(
    ((totalInvestmentCashFlows - policyTotalCost) / policyTotalCost) * 100,
  );

  if (percentDifference > 1) {
    console.error('Investment cash flows do not match policy total cost', {
      totalInvestmentCashFlows,
      policyTotalCost,
      difference: totalInvestmentCashFlows - policyTotalCost,
      percentDifference: `${percentDifference.toFixed(4)}%`,
      cashFlows: investmentCashFlows.map(cf => ({
        year: cf.year,
        amount: cf.amount,
        runningTotal: investmentCashFlows
          .filter(f => f.year <= cf.year)
          .reduce((sum, f) => sum + f.amount, 0),
      })),
    });
  }
}

function returnCashflowEqualsPolicyTotalValue(
  returnCashFlows: CashFlow[],
  policyTotalValue: number | undefined,
) {
  if (isNullOrUndefined(policyTotalValue)) {
    return;
  }

  // Sum all return cash flows
  const totalReturnCashFlows = returnCashFlows.reduce((sum, cf) => sum + cf.amount, 0);

  // Calculate percentage difference
  const percentDifference = Math.abs(
    ((totalReturnCashFlows - policyTotalValue) / policyTotalValue) * 100,
  );

  if (percentDifference > 1) {
    console.error('Return cash flows do not match policy total value', {
      totalReturnCashFlows,
      policyTotalValue,
      difference: totalReturnCashFlows - policyTotalValue,
      percentDifference: `${percentDifference.toFixed(4)}%`,
      cashFlows: returnCashFlows.map(cf => ({
        year: cf.year,
        amount: cf.amount,
        runningTotal: returnCashFlows
          .filter(f => f.year <= cf.year)
          .reduce((sum, f) => sum + f.amount, 0),
      })),
    });
  }
}

export function calcPolicyTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyPremiumMonthlyCost,
        policyPremiumStartYear,
        policyLimitedPayYears,
        policyType,
        policyPremiumIncreaseRate,
        policyPremiumIncreaseYears,
      },
    },
    appliedInferenceSet: { ltcAtYear },
  } = mutableClient;

  if (isNullOrUndefined(policyPremiumStartYear)) {
    ltcPolicy.policyTotalCost = 0;
    return;
  }

  const premiumInvestmentEndYear =
    policyType === FundingPolicyType.fortifiedLife ? calcLtcEndYear(mutableClient) : ltcAtYear;

  const totalYears =
    calculateMonthsBetweenYears(policyPremiumStartYear, premiumInvestmentEndYear!) / 12;

  // Determine the period for premium payments
  const premiumInvestmentPeriodYears = policyLimitedPayYears
    ? Math.min(policyLimitedPayYears, totalYears)
    : totalYears;

  ltcPolicy.premiumInvestmentPeriodMonths = premiumInvestmentPeriodYears * 12;

  // Calculate the monthly premium total cost
  const monthlyAmount = policyPremiumMonthlyCost ?? 0;
  const hasIncreaseRate = policyPremiumIncreaseRate && policyPremiumIncreaseRate > 0;

  let monthlyPremiumTotalCost: number;

  if (!hasIncreaseRate) {
    // Simple calculation without annual increase
    monthlyPremiumTotalCost = premiumInvestmentPeriodYears * 12 * monthlyAmount;
  } else {
    // Determine years to apply the increase rate
    const appliedPolicyPremiumIncreaseYears =
      policyPremiumIncreaseYears && policyPremiumIncreaseYears > 0
        ? Math.min(policyPremiumIncreaseYears, premiumInvestmentPeriodYears)
        : premiumInvestmentPeriodYears;

    ltcPolicy.appliedPolicyPremiumIncreaseYears = appliedPolicyPremiumIncreaseYears;

    const { total } = calcTotalWithAnnualIncrease(
      monthlyAmount,
      policyPremiumIncreaseRate,
      appliedPolicyPremiumIncreaseYears,
      premiumInvestmentPeriodYears,
    );
    monthlyPremiumTotalCost = total;
  }

  ltcPolicy.monthlyPremiumTotalCost = Math.max(monthlyPremiumTotalCost, 0);

  ltcPolicy.policyTotalCost =
    ltcPolicy.monthlyPremiumTotalCost +
    (isNullOrUndefined(ltcPolicy.policyLumpSumPayment) || ltcPolicy.policyLumpSumPayment < 0
      ? 0
      : ltcPolicy.policyLumpSumPayment);
}

function copyJointPolicyToClientPartner(mutableClient: Draft<Client>): void {
  const {
    fundingSources: { ltcPolicy },
    mutableClientPartner,
  } = mutableClient;

  if (!isJointPolicy(ltcPolicy.policyType, mutableClientPartner)) {
    return;
  }
  const ltcPolicyCopy = cloneDeep(ltcPolicy);
  mutableClientPartner.fundingSources.ltcPolicy = ltcPolicyCopy;
}

type ClientSinglePhaseCosts = {
  client: Client;
  phaseStartYear: number | null;
  phaseCosts: SinglePhaseCosts;
};

export function combineSortClientPartnerPhaseCosts(
  mutableClient: Draft<Client>,
): ClientSinglePhaseCosts[] {
  const { phaseCosts } = mutableClient;

  if (!mutableClient.mutableClientPartner) {
    return [];
  }
  // extract partner data
  const mutableClientPartner = mutableClient.mutableClientPartner;
  const partnerPhaseCosts = mutableClientPartner.phaseCosts;

  // phase start years
  const clientPhaseStartYears = calcClientPhaseStartYears(mutableClient);
  const partnerPhaseStartYears = calcClientPhaseStartYears(mutableClientPartner);

  // order client and partner phases chronologically
  const combinedPhaseCosts: Array<ClientSinglePhaseCosts> = [
    ...phaseCosts.map(pc => ({
      client: mutableClient,
      phaseStartYear: clientPhaseStartYears[pc.carePhase],
      phaseCosts: pc,
    })),
    ...partnerPhaseCosts.map(pc => ({
      client: mutableClientPartner,
      phaseStartYear: partnerPhaseStartYears[pc.carePhase],
      phaseCosts: pc,
    })),
  ];

  combinedPhaseCosts.sort((a, b) => {
    const startYearA = a.phaseStartYear ?? Infinity;
    const startYearB = b.phaseStartYear ?? Infinity;

    return startYearA - startYearB;
  });

  return combinedPhaseCosts.filter(
    clientPhaseCost =>
      !isCarePhasePast(clientPhaseCost.phaseCosts.carePhase, clientPhaseCost.client),
  );
}
export function calcMultiplePolicyTotalFields(mutableClient: WritableDraft<Client>) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
  } = mutableClient;

  const activeLtcPolicies = Object.values(multipleFundingSources.ltcPolicy).filter(
    ltcPolicy => !ltcPolicy.isDeactivated,
  );

  multipleFundingSources.multiplePolicyTotalValue = sumPropertyForAllPolicies(
    'policyTotalValue',
    activeLtcPolicies,
  );
  multipleFundingSources.multiplePolicyTotalCost = sumPropertyForAllPolicies(
    'policyTotalCost',
    activeLtcPolicies,
  );
  multipleFundingSources.multiplePolicyNonLtcPayout = sumPropertyForAllPolicies(
    'policyNonLtcPayout',
    activeLtcPolicies,
  );

  multipleFundingSources.multiplePolicyProjectedCostCoverage = sumPropertyForAllPolicies(
    clientHasStartedLtc ? 'policyCoverageOfRemainingLtcCosts' : 'projectedCostCoverage',
    activeLtcPolicies,
  );

  // check for any unlimited policies
  const unlimitedPolicies = activeLtcPolicies.filter(policy => policy.policyBenefitIsUnlimited);

  multipleFundingSources.multiplePolicyTotalLtcBenefit =
    unlimitedPolicies.length > 0
      ? Infinity
      : sumPropertyForAllPolicies('policyTotalLTCBenefit', activeLtcPolicies);
}

function sumPropertyForAllSelfFundingSources(
  property: keyof SelfFundingSource,
  selfFundingSources: WritableDraft<SelfFundingSource>[],
): number | undefined {
  // check if all values are undefined
  if (selfFundingSources.every(source => source[property] === undefined)) {
    return undefined;
  }
  return selfFundingSources.reduce((acc, source) => {
    const value = source[property];
    return acc + (typeof value === 'number' ? value : 0);
  }, 0);
}

export function calcMultipleSelfFundingTotalFields(mutableClient: WritableDraft<Client>) {
  const { multipleFundingSources } = mutableClient;

  const activeSelfFundingSources = Object.values(multipleFundingSources.selfFunding).filter(
    selfFunding => !selfFunding.isDeactivated,
  );

  multipleFundingSources.multipleSelfFundingTotalValue = sumPropertyForAllSelfFundingSources(
    'selfFundingTotalValue',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingTotalCost = sumPropertyForAllSelfFundingSources(
    'selfFundingTotalCost',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingNonLtcPayout = sumPropertyForAllSelfFundingSources(
    'selfFundingNonLtcPayout',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingProjectedCostCoverage =
    sumPropertyForAllSelfFundingSources(
      'selfFundingProjectedCostCoverage',
      activeSelfFundingSources,
    );
}

function convertPolicyPremiumsToCashFlows(ltcPolicy: PolicyFundingSource): CashFlow[] {
  const {
    policyLumpSumPayment,
    policyPremiumMonthlyCost,
    premiumInvestmentPeriodMonths,
    policyPremiumIncreaseRate,
    appliedPolicyPremiumIncreaseYears,
  } = ltcPolicy;
  const cashFlows: CashFlow[] = [];

  // Add lump sum payment at year 0 if it exists
  if (!isNullOrUndefined(policyLumpSumPayment) && policyLumpSumPayment > 0) {
    cashFlows.push({
      year: 0,
      amount: policyLumpSumPayment,
    });
  }

  // If no monthly premiums or investment period, return just the lump sum
  if (
    isNullOrUndefined(policyPremiumMonthlyCost) ||
    isNullOrUndefined(premiumInvestmentPeriodMonths) ||
    policyPremiumMonthlyCost <= 0
  ) {
    return cashFlows;
  }

  const fullYears = Math.floor(premiumInvestmentPeriodMonths / 12);
  const remainingMonths = Math.round(premiumInvestmentPeriodMonths % 12);
  const monthlyAmount = policyPremiumMonthlyCost;
  const hasIncreaseRate = policyPremiumIncreaseRate && policyPremiumIncreaseRate > 0;

  if (!hasIncreaseRate) {
    // Simple calculation without annual increase
    for (let year = 0; year < fullYears; year++) {
      cashFlows.push({
        year: year + 1,
        amount: monthlyAmount * 12,
      });
    }
    if (remainingMonths > 0) {
      cashFlows.push({
        year: fullYears + 1,
        amount: monthlyAmount * remainingMonths,
      });
    }
  } else {
    // Calculate with premium increase
    const { cashflows } = calcTotalWithAnnualIncrease(
      monthlyAmount,
      policyPremiumIncreaseRate,
      appliedPolicyPremiumIncreaseYears ?? fullYears,
      fullYears,
    );

    cashflows.forEach(cf => cashFlows.push(cf));

    // Add partial year if any
    if (remainingMonths > 0) {
      const finalMonthlyAmount =
        monthlyAmount *
        Math.pow(
          1 + policyPremiumIncreaseRate,
          Math.min(appliedPolicyPremiumIncreaseYears ?? fullYears, fullYears),
        );
      cashFlows.push({
        year: fullYears + 1,
        amount: finalMonthlyAmount * remainingMonths,
      });
    }
  }

  return cashFlows;
}

function convertPolicyReturnCashFlow(mutableClient: Draft<Client>): CashFlow[] {
  const {
    clientPhasePredictedEndYears,
    phaseCosts,
    fundingSources: {
      ltcPolicy: { policyTotalValue, policyPremiumStartYear, policyType },
    },
    mutableClientPartner,
  } = mutableClient;

  const phaseStartYears = calcClientPhaseStartYears(mutableClient);

  // Early return if required values are missing
  if (isNullOrUndefined(policyTotalValue) || isNullOrUndefined(policyPremiumStartYear)) {
    return [];
  }

  const allCarePhases = [CarePhase.earlyCare, CarePhase.moderateCare, CarePhase.fullCare];
  const jointPolicy = isJointPolicy(policyType, mutableClientPartner);

  // Get phases with coverage for a client
  const getPhasesWithCoverage = (client: Draft<Client>) => {
    // Early return if phaseCalculations is undefined or empty
    if (!client.phaseCalculations || !Array.isArray(client.phaseCalculations)) {
      return [];
    }

    return allCarePhases.filter(phase => {
      const phaseCalc = client.phaseCalculations.find(
        calc => calc && typeof calc === 'object' && calc.phase === phase,
      );
      return phaseCalc && 'phaseCostCoverage' in phaseCalc && phaseCalc.phaseCostCoverage > 0;
    });
  };

  // Get client's phases with coverage
  let clientPhasesWithCoverage = getPhasesWithCoverage(mutableClient);

  // Handle single policy case
  if (!jointPolicy || !mutableClientPartner) {
    if (clientPhasesWithCoverage.length === 0 && !policyTotalValue) return [];

    // If no phases with coverage, but policy total value exists, assume all returns are after last phase
    if (clientPhasesWithCoverage.length === 0) {
      clientPhasesWithCoverage = [CarePhase.fullCare];
    }
    const valuePerPhase = policyTotalValue / clientPhasesWithCoverage.length;
    return createCashFlowsForPhasePayout({
      phases: clientPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: clientPhasePredictedEndYears,
      predictedStartYears: phaseStartYears,
      phaseCosts,
      policyPremiumStartYear,
    });
  }

  // Handle joint policy case
  const partnerPhasesWithCoverage = getPhasesWithCoverage(mutableClientPartner);
  const allPhasesWithCoverage = [...clientPhasesWithCoverage, ...partnerPhasesWithCoverage];
  const partnerPhaseStartYears = calcClientPhaseStartYears(mutableClientPartner);

  if (allPhasesWithCoverage.length === 0) return [];

  const valuePerPhase = policyTotalValue / allPhasesWithCoverage.length;
  return [
    ...createCashFlowsForPhasePayout({
      phases: clientPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: clientPhasePredictedEndYears,
      predictedStartYears: phaseStartYears,
      phaseCosts,
      policyPremiumStartYear,
    }),
    ...createCashFlowsForPhasePayout({
      phases: partnerPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: mutableClientPartner.clientPhasePredictedEndYears,
      predictedStartYears: partnerPhaseStartYears,
      phaseCosts: mutableClientPartner.phaseCosts,
      policyPremiumStartYear,
    }),
  ];
}

// Helper function to create cash flows for a set of phases
interface CreateCashFlowsParams {
  phases: CarePhase[];
  valuePerPhase: number;
  predictedEndYears: Record<CarePhase, number | null>;
  predictedStartYears: Record<CarePhase, number | null>;
  phaseCosts: SinglePhaseCosts[];
  policyPremiumStartYear: number;
}

function createCashFlowsForPhasePayout({
  phases,
  valuePerPhase,
  predictedEndYears,
  predictedStartYears,
  phaseCosts,
  policyPremiumStartYear,
}: CreateCashFlowsParams): CashFlow[] {
  const cashFlows: CashFlow[] = [];

  phases.forEach(phase => {
    const endYear = predictedEndYears[phase];
    const startYear = predictedStartYears[phase];

    if (isNullOrUndefined(endYear) || isNullOrUndefined(startYear)) {
      return;
    }

    // Find the phase cost data to get duration in months
    const phaseCostData = phaseCosts.find(pc => pc.carePhase === phase);
    const durationMonths = phaseCostData?.phaseCareMonthsNeeded ?? 0;

    if (durationMonths === 0) {
      // If no duration data, fall back to original lump sum at end year
      cashFlows.push({
        year: Math.max(0, Math.ceil(endYear - policyPremiumStartYear)),
        amount: valuePerPhase,
      });
      return;
    }

    // Spread payout over the phase duration
    const durationYears = durationMonths / 12;
    const numYears = Math.ceil(durationYears);
    const yearlyPayout = valuePerPhase / durationYears;

    for (let yearOffset = 0; yearOffset < numYears; yearOffset++) {
      const payoutYear = startYear + yearOffset;
      const isLastYear = yearOffset === numYears - 1;

      // In the last year, pay the remainder to avoid rounding issues
      const amount = isLastYear ? valuePerPhase - yearlyPayout * yearOffset : yearlyPayout;

      cashFlows.push({
        year: Math.max(0, Math.round(payoutYear - policyPremiumStartYear)),
        amount: amount,
      });
    }
  });

  return cashFlows;
}

export type ReturnOnInvestmentCalculations = {
  investments: CashFlow[];
  returns: CashFlow[];
  investmentPeriodYears: number;
};

export function calcReturnOnInvestment(mutableClient: WritableDraft<Client>) {
  const {
    multipleFundingSources,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
  } = mutableClient;
  const { combinedTotalValue, combinedFundingTotalCost } = mutableClient.fundingSources;
  const { fundingSources } = mutableClient;
  const discountRate = mutableClient.clientCalculationSettings?.discountRate ?? undefined;

  // Calculate self-funding ROI by aggregating all self-funding sources
  const selfFundingInvestments: CashFlow[] = [];
  const selfFundingReturns: CashFlow[] = [];
  const selfFundingInvestmentPeriodYears: number[] = [];
  const selfFundingSources = Object.entries(multipleFundingSources.selfFunding);

  selfFundingSources.forEach(([_fundingSourceId, selfFundingSource]) => {
    if (selfFundingSource.isDeactivated) return;

    // Temporarily set the selfFunding source to calculate its cash flows
    mutableClient.fundingSources.selfFunding = cloneDeep(selfFundingSource);

    const { investments, returns, investmentPeriodYears } = calcSelfFundingProjectedROI(
      mutableClient,
      discountRate,
    );

    selfFundingInvestments.push(...investments);
    selfFundingReturns.push(...returns);
    selfFundingInvestmentPeriodYears.push(investmentPeriodYears);
  });

  const {
    investments: annuityInvestments,
    returns: annuityReturns,
    investmentPeriodYears: annuityInvestmentPeriodYears,
  } = calcAnnuityProjectedROI(mutableClient, discountRate);

  // Calculate LTC policy ROI
  const ltcPolicyInvestments: CashFlow[] = [];
  const ltcPolicyReturns: CashFlow[] = [];
  const ltcPolicyInvestmentPeriodYears: number[] = [];
  const ltcPolicies = Object.entries(multipleFundingSources.ltcPolicy);
  ltcPolicies.forEach(([_fundingSourceId, policyFundingSource]) => {
    if (policyFundingSource.isDeactivated) {
      return;
    }
    ltcPolicyInvestments.push(...(policyFundingSource.investmentCashFlows ?? []));
    ltcPolicyReturns.push(...(policyFundingSource.returnCashFlows ?? []));
    ltcPolicyInvestmentPeriodYears.push(policyFundingSource.investmentPeriodYears ?? 0);
  });

  // Calculate combined ROI
  if (!combinedFundingTotalCost || !combinedTotalValue || combinedFundingTotalCost === 0) {
    fundingSources.combinedProjectedReturnOnInvestment = 0;
    fundingSources.combinedProjectedROIAnnualized = 0;
    fundingSources.combinedProjectedInternalRateOfReturn = 0;
    return;
  }

  const policyInvestmentPeriods = Object.values(multipleFundingSources.ltcPolicy)
    .filter(policy => !policy.isDeactivated)
    .map(policy => policy.investmentPeriodYears ?? 0);

  const maximumInvestmentPeriodYears = Math.max(
    ...policyInvestmentPeriods,
    ...selfFundingInvestmentPeriodYears,
    annuityInvestmentPeriodYears ?? 0,
    0,
  );

  fundingSources.combinedInvestmentPeriodYears = maximumInvestmentPeriodYears;
  fundingSources.combinedProjectedReturnOnInvestment =
    (combinedTotalValue +
      allPhaseInflatedProfessionalShareCostDiscountAmount -
      combinedFundingTotalCost) /
    combinedFundingTotalCost;

  // Find the earliest base year among all funding sources
  const policyStartYears = Object.values(multipleFundingSources.ltcPolicy)
    .filter(policy => !policy.isDeactivated && policy.policyPremiumStartYear)
    .map(policy => policy.policyPremiumStartYear!);

  const currentYear = new Date().getFullYear();
  const earliestBaseYear =
    policyStartYears.length > 0 ? Math.min(...policyStartYears, currentYear) : currentYear;

  const analysis = analyzeInvestmentReturns(
    [...selfFundingInvestments, ...annuityInvestments, ...ltcPolicyInvestments],
    [...selfFundingReturns, ...annuityReturns, ...ltcPolicyReturns],
    discountRate,
    earliestBaseYear,
  );

  fundingSources.combinedProjectedCompoundAnnualGrowthRate = analysis.cagr;
  fundingSources.combinedProjectedInternalRateOfReturn = analysis.internalRateOfReturn;
}
